import {
    StockStatus,
    WarehouseStockDetails,
} from '~/lib/data-contract/auto-generated/shop-api-model';
import { useStockDetails } from '~/services/shop/hooks';
import { useTranslations } from '~/services/umbraco/hooks';
import { ProductOnlineStatus } from '~/shared/components/ProductOnlineStatus';
import { useIsMounted } from '~/shared/hooks';
import { usePostalCode } from '~/shared/utils/storage';
import { openModalFreight, openQuickAddModal, setItemNo } from '~/state/useModalFreight';
import {
    DeliveryStatus,
    DeliveryText,
    PickUpText,
    StatusWrapper,
    StyledButton,
    StyledInfoText,
    Wrapper,
} from '../styled';
import { ProductStatusProps } from '../types';
import Loading from './Loading';

export default function PickUpStock({
    canCreateOrder,
    itemNo,
    activeWarehouseId,
    productStatus,
    modalType,
}: ProductStatusProps) {
    const { translate } = useTranslations();
    const isMounted = useIsMounted();
    const [postalCode] = usePostalCode();
    const { data: stockDetails, isLoading, isError } = useStockDetails({ itemNo, postalCode });

    const warehouses = stockDetails?.pickup?.warehouses;

    const itemsInStock = warehouses?.filter(
        (warehouse) => warehouse.status === 'InStock' || warehouse.status === 'LowStock',
    ).length;

    if (isLoading && !isError) {
        return <Loading isActive={isMounted()} />;
    }

    if (isError || !stockDetails?.pickup || productStatus === 'Obsolete' || itemsInStock === 0) {
        return (
            <Wrapper noStockDetails isActive={false}>
                <StyledInfoText>{translate('product.pickup.notAvailable')}</StyledInfoText>
            </Wrapper>
        );
    }

    const { title, status } =
        warehouses?.find(({ id }) => id === activeWarehouseId) || stockDetails.pickup;

    const countStatus = (warehouses: WarehouseStockDetails[] | undefined) => {
        let inStockCount = 0;
        let lowStockCount = 0;

        warehouses?.forEach((warehouse) => {
            if (warehouse.status === 'InStock') {
                inStockCount++;
            } else if (warehouse.status === 'LowStock') {
                lowStockCount++;
            }
        });

        if (inStockCount === 0 && lowStockCount === 0) {
            return 'OutOfStock';
        }

        return inStockCount >= lowStockCount ? 'InStock' : 'LowStock';
    };

    const modalHandler = () => {
        switch (modalType) {
            case 'quickAdd':
                openQuickAddModal();
                break;
            case 'freight':
                openModalFreight();
                break;
            default:
                console.error(`Invalid modal type: ${modalType}`);
                break;
        }
    };

    return (
        <StatusWrapper showOnlineStock={canCreateOrder}>
            {status ? (
                <DeliveryStatus>
                    <ProductOnlineStatus status={countStatus(warehouses) as StockStatus} />
                </DeliveryStatus>
            ) : null}
            {warehouses ? (
                <DeliveryText>
                    {translate('freightCalculator.inStoreStock').replace(
                        '[ITEM_STOCK]',
                        itemsInStock?.toString() ?? '0',
                    )}{' '}
                    <StyledButton
                        onClick={() => {
                            setItemNo(itemNo ?? '');
                            modalHandler();
                        }}
                    >
                        (
                        {canCreateOrder
                            ? translate('freightCalculator.chooseStore')
                            : translate('freightCalculator.viewStoreStock')}
                        )
                    </StyledButton>
                </DeliveryText>
            ) : null}

            {canCreateOrder ? (
                <PickUpText>{translate(title?.name, title?.values)}</PickUpText>
            ) : null}
        </StatusWrapper>
    );
}
